import { GameState, GameStats, GameProgress, WordLength, Tile } from '../types/game'
import type { StatsResult } from '../services/account/statsApi'
import { getDayOffset } from './dateUtils'

const STORAGE_KEYS = {
  GAME_SETTINGS: 'gameSettings',
  GAME_STATS: 'gameStats',
  LAST_PLAYED: 'lastPlayed',
  CURRENT_GAME: 'currentGame',
  PLAYED_MODES: 'playedModes',
  COMPLETED_GAMES_DETAIL: 'completedGamesDetail',
  GUEST_NAME: 'guestName',
  // Anagram gets its own namespaced keys rather than parameterizing the
  // Wordle functions above - avoids collisions (both games can have a
  // "5-letter" mode) without touching working Wordle storage code. Worth
  // revisiting/parameterizing once a 3rd game shows the pattern is real.
  ANAGRAM_PLAYED_MODES: 'anagramPlayedModes',
  ANAGRAM_COMPLETED_DETAIL: 'anagramCompletedDetail',
  DAILY_TIME_STATS: 'dailyTimeStats',
  TIME_RECORDED_KEYS: 'timeRecordedKeys',
  SAVE_PROMPT_DISMISSED_AT: 'savePromptDismissedAt',
  CACHED_STATS: 'cachedStats',
  PASSKEY_OFFER_DISMISSED_AT: 'passkeyOfferDismissedAt',
} as const

// Type-safe storage operations with error handling
export const storage = {
  getItem: <T>(key: string, defaultValue: T): T => {
    try {
      const item = localStorage.getItem(key)
      return item ? JSON.parse(item) : defaultValue
    } catch (error) {
      console.error(`Error reading from storage: ${key}`, error)
      return defaultValue
    }
  },

  setItem: <T>(key: string, value: T): void => {
    try {
      localStorage.setItem(key, JSON.stringify(value))
    } catch (error) {
      console.error(`Error writing to storage: ${key}`, error)
    }
  },
}

// Default values
export const DEFAULT_STATS: GameStats = {
  gamesPlayed: 0,
  gamesWon: 0,
  currentStreak: 0,
  maxStreak: 0,
  guessDistribution: {},
}

// Interface for tracking played modes
interface PlayedModes {
  day: number
  modes: WordLength[]
}

// Storage utilities
export const getGameSettings = (): GameState | null =>
  storage.getItem(STORAGE_KEYS.GAME_SETTINGS, null)

export const setGameSettings = (settings: GameState): void =>
  storage.setItem(STORAGE_KEYS.GAME_SETTINGS, settings)

export const getGameStats = (): GameStats =>
  storage.getItem(STORAGE_KEYS.GAME_STATS, DEFAULT_STATS)

export const setGameStats = (stats: GameStats): void =>
  storage.setItem(STORAGE_KEYS.GAME_STATS, stats)

export const getLastPlayed = (): number =>
  storage.getItem(STORAGE_KEYS.LAST_PLAYED, 0)

export const setLastPlayed = (timestamp: number): void =>
  storage.setItem(STORAGE_KEYS.LAST_PLAYED, timestamp)

export const getGuestName = (): string | null =>
  storage.getItem(STORAGE_KEYS.GUEST_NAME, null)

export const setGuestName = (name: string): void =>
  storage.setItem(STORAGE_KEYS.GUEST_NAME, name)

/** When the player last chose "Not now" on the save-progress prompt, or null if never. */
export const getSavePromptDismissedAt = (): number | null =>
  storage.getItem<number | null>(STORAGE_KEYS.SAVE_PROMPT_DISMISSED_AT, null)

export const setSavePromptDismissedAt = (timestamp: number): void =>
  storage.setItem(STORAGE_KEYS.SAVE_PROMPT_DISMISSED_AT, timestamp)

/** When the player last chose "Not now" on the passkey offer, or null if never. */
export const getPasskeyOfferDismissedAt = (): number | null =>
  storage.getItem<number | null>(STORAGE_KEYS.PASSKEY_OFFER_DISMISSED_AT, null)

export const setPasskeyOfferDismissedAt = (timestamp: number): void =>
  storage.setItem(STORAGE_KEYS.PASSKEY_OFFER_DISMISSED_AT, timestamp)

/** The server's last stats answer, kept only so the streak card can paint before the network answers. */
export const getCachedStats = (): StatsResult | null =>
  storage.getItem<StatsResult | null>(STORAGE_KEYS.CACHED_STATS, null)

export const setCachedStats = (stats: StatsResult): void =>
  storage.setItem(STORAGE_KEYS.CACHED_STATS, stats)

export const getCurrentGame = (): GameProgress | null =>
  storage.getItem(STORAGE_KEYS.CURRENT_GAME, null)

export const setCurrentGame = (progress: GameProgress): void =>
  storage.setItem(STORAGE_KEYS.CURRENT_GAME, progress)

export const clearCurrentGame = (): void =>
  localStorage.removeItem(STORAGE_KEYS.CURRENT_GAME)

/**
 * Get the list of modes the user has played today
 * @returns Array of word lengths representing played modes
 */
export const getPlayedModes = (): WordLength[] => {
  const currentDay = getDayOffset()
  const playedModes: PlayedModes = storage.getItem(STORAGE_KEYS.PLAYED_MODES, {
    day: currentDay,
    modes: [],
  })

  // Reset if it's a new day
  if (playedModes.day !== currentDay) {
    return []
  }

  return playedModes.modes
}

/**
 * Mark a mode as played for today
 * @param mode Word length of the played mode
 */
export const markModeAsPlayed = (mode: WordLength): void => {
  const currentDay = getDayOffset()
  const playedModes = getPlayedModes()

  // Add the mode if not already in the list
  if (!playedModes.includes(mode)) {
    const updatedModes: PlayedModes = {
      day: currentDay,
      modes: [...playedModes, mode],
    }

    storage.setItem(STORAGE_KEYS.PLAYED_MODES, updatedModes)
  }
}

/**
 * Check if a specific mode has been played today
 * @param mode Word length to check
 * @returns Boolean indicating if the mode has been played today
 */
export const hasModeBeenPlayed = (mode: WordLength): boolean => {
  return getPlayedModes().includes(mode)
}

/**
 * Anagram's played-modes tracking, mirroring getPlayedModes/markModeAsPlayed/
 * hasModeBeenPlayed above but under a separate storage key so a "5-letter"
 * Anagram play today doesn't collide with a "5-letter" Wordle play.
 */
export const getAnagramPlayedModes = (): WordLength[] => {
  const currentDay = getDayOffset()
  const playedModes: PlayedModes = storage.getItem(STORAGE_KEYS.ANAGRAM_PLAYED_MODES, {
    day: currentDay,
    modes: [],
  })

  if (playedModes.day !== currentDay) {
    return []
  }

  return playedModes.modes
}

export const markAnagramModeAsPlayed = (mode: WordLength): void => {
  const currentDay = getDayOffset()
  const playedModes = getAnagramPlayedModes()

  if (!playedModes.includes(mode)) {
    const updatedModes: PlayedModes = {
      day: currentDay,
      modes: [...playedModes, mode],
    }

    storage.setItem(STORAGE_KEYS.ANAGRAM_PLAYED_MODES, updatedModes)
  }
}

export const hasAnagramModeBeenPlayed = (mode: WordLength): boolean => {
  return getAnagramPlayedModes().includes(mode)
}

export interface AnagramCompletedDetail {
  word: string
  won: boolean
}

interface AnagramCompletedDetailMap {
  day: number
  games: Record<number, AnagramCompletedDetail>
}

/**
 * Anagram's per-mode "revealed word" store, separate from
 * CompletedGameDetail (Wordle's Tile[][] guesses) since Anagram attempts
 * are freeform text, not per-position tile states - there's nothing to
 * color per-letter, only a final word + win/loss outcome to reveal.
 */
export const getAnagramCompletedDetail = (): Record<number, AnagramCompletedDetail> | null => {
  const currentDay = getDayOffset()
  const detail = storage.getItem<AnagramCompletedDetailMap>(STORAGE_KEYS.ANAGRAM_COMPLETED_DETAIL, {
    day: currentDay,
    games: {},
  })
  if (detail.day !== currentDay) {
    return null
  }
  return detail.games
}

export const saveAnagramCompletedDetail = (mode: WordLength, word: string, won: boolean): void => {
  const currentDay = getDayOffset()
  const currentDetail = storage.getItem<AnagramCompletedDetailMap>(
    STORAGE_KEYS.ANAGRAM_COMPLETED_DETAIL,
    { day: currentDay, games: {} }
  )

  const games = currentDetail.day === currentDay ? currentDetail.games : {}
  games[mode] = { word, won }

  storage.setItem(STORAGE_KEYS.ANAGRAM_COMPLETED_DETAIL, {
    day: currentDay,
    games,
  })
}

export interface CompletedGameDetail {
  guesses: Tile[][]
  won: boolean
  targetWord: string
}

interface CompletedGamesMap {
  day: number
  games: Record<number, CompletedGameDetail>
}

/**
 * Get the details of completed games for today
 */
export const getCompletedGamesDetail = (): Record<number, CompletedGameDetail> | null => {
  const currentDay = getDayOffset()
  const detail: CompletedGamesMap = storage.getItem(STORAGE_KEYS.COMPLETED_GAMES_DETAIL, {
    day: currentDay,
    games: {},
  })
  if (detail.day !== currentDay) {
    return null
  }
  return detail.games
}

/**
 * Save details of a completed game
 */
export const saveCompletedGameDetail = (
  mode: WordLength,
  guesses: Tile[][],
  won: boolean,
  targetWord: string
): void => {
  const currentDay = getDayOffset()
  const currentDetail = storage.getItem<CompletedGamesMap>(STORAGE_KEYS.COMPLETED_GAMES_DETAIL, {
    day: currentDay,
    games: {},
  })

  const games = currentDetail.day === currentDay ? currentDetail.games : {}
  games[mode] = { guesses, won, targetWord }

  storage.setItem(STORAGE_KEYS.COMPLETED_GAMES_DETAIL, {
    day: currentDay,
    games,
  })
}

const DAILY_TIME_STATS_MAX_AGE_DAYS = 60

// Local calendar date (YYYY-MM-DD), distinct from getDayOffset()'s epoch
// offset - the daily time chart needs real dates to derive weekday labels.
const getLocalDateKey = (date: Date): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const pruneDailyTimeStats = (
  stats: Record<string, number>
): Record<string, number> => {
  const cutoff = new Date()
  cutoff.setDate(cutoff.getDate() - DAILY_TIME_STATS_MAX_AGE_DAYS)
  const cutoffKey = getLocalDateKey(cutoff)

  return Object.fromEntries(
    Object.entries(stats).filter(([date]) => date >= cutoffKey)
  )
}

interface TimeRecordedKeys {
  day: number
  keys: string[]
}

/**
 * Guards recordTimeSpent against recording the same completion twice for
 * the same calendar day - e.g. React.StrictMode double-invoking the
 * setStats updater that calls it, or a page reload resuming an
 * already-completed session via the backfill path in useWordle.ts/
 * useAnagram.ts. `dedupeKey` should uniquely identify one day's worth of
 * one mode of one game, e.g. "wordle-5" or "anagram-5".
 */
const hasTimeBeenRecordedToday = (dedupeKey: string): boolean => {
  const currentDay = getDayOffset()
  const recorded = storage.getItem<TimeRecordedKeys>(STORAGE_KEYS.TIME_RECORDED_KEYS, {
    day: currentDay,
    keys: [],
  })
  if (recorded.day !== currentDay) return false
  return recorded.keys.includes(dedupeKey)
}

const markTimeRecordedToday = (dedupeKey: string): void => {
  const currentDay = getDayOffset()
  const recorded = storage.getItem<TimeRecordedKeys>(STORAGE_KEYS.TIME_RECORDED_KEYS, {
    day: currentDay,
    keys: [],
  })
  const keys = recorded.day === currentDay ? recorded.keys : []
  storage.setItem(STORAGE_KEYS.TIME_RECORDED_KEYS, {
    day: currentDay,
    keys: [...keys, dedupeKey],
  })
}

/**
 * Accumulate seconds spent solving a challenge into today's bucket, keyed
 * by local calendar date. Unlike COMPLETED_GAMES_DETAIL/PLAYED_MODES above
 * (which reset every day by design), this store keeps history for the
 * "time spent solving" chart on the Statistics page.
 *
 * No-ops if `dedupeKey` (one mode of one game, e.g. "wordle-5") was already
 * recorded today - see hasTimeBeenRecordedToday.
 */
export const recordTimeSpent = (seconds: number, dedupeKey: string): void => {
  if (hasTimeBeenRecordedToday(dedupeKey)) return

  const stats = storage.getItem<Record<string, number>>(
    STORAGE_KEYS.DAILY_TIME_STATS,
    {}
  )
  const todayKey = getLocalDateKey(new Date())
  stats[todayKey] = (stats[todayKey] || 0) + seconds

  storage.setItem(STORAGE_KEYS.DAILY_TIME_STATS, pruneDailyTimeStats(stats))
  markTimeRecordedToday(dedupeKey)
}

export interface DailyTimeStat {
  date: string
  seconds: number
}

/**
 * Returns the last `days` calendar days (including today, oldest first),
 * filling in zero-value days with no recorded play, for a fixed-width chart.
 */
export const getDailyTimeStats = (days: number): DailyTimeStat[] => {
  const stats = storage.getItem<Record<string, number>>(
    STORAGE_KEYS.DAILY_TIME_STATS,
    {}
  )

  const result: DailyTimeStat[] = []
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date()
    date.setDate(date.getDate() - i)
    const key = getLocalDateKey(date)
    result.push({ date: key, seconds: stats[key] || 0 })
  }

  return result
}
